import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { useLogEvent } from "@/services/Analytics/useLogEvent";
import { ClickTarget, SourcePage } from "@/services/Analytics/EventTypes";
import { postClose, useHeaderContext } from "@WahooFitness/wahoo-offline-mfe";
import { EducationStepContent } from "./types";
import useLocalStorage from "@/hooks/useLocalStorage";

function useEducationFlow({
  steps,
  isStandAlone,
  setPage,
  finishedEducation,
  setFinishedEducation,
  closeOnBack,
  analyticsEventName,
  analyticsSourcePage,
  closePortalId,
  nextPage,
  localStorageKey,
}: {
  steps: EducationStepContent[];
  isStandAlone: boolean;
  setPage?: (page: number) => void;
  finishedEducation?: boolean;
  setFinishedEducation?: (finishedEducation: boolean) => void;
  closeOnBack?: boolean;
  analyticsEventName: string;
  analyticsSourcePage: SourcePage;
  closePortalId: string;
  nextPage: number;
  localStorageKey: string;
}) {
  const [activeStep, setActiveStep] = useState(finishedEducation ? steps.length - 1 : 0);
  const { navigateBack } = useHeaderContext();
  const { logEvent } = useLogEvent();
  const bodyRef = useRef<HTMLElement | null>(null);
  const { setValue: setHasSeenFlow } = useLocalStorage(localStorageKey);

  // scroll to top of page when active step changes
  useLayoutEffect(() => {
    if (activeStep === 0) {
      bodyRef.current?.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } else {
      bodyRef.current?.scrollIntoView();
    }
  }, [activeStep]);

  const logEducationEvent = useCallback(
    (clickTarget: ClickTarget) => {
      logEvent(analyticsEventName, {
        click_target: clickTarget,
        source_page_name: analyticsSourcePage,
        step_name: steps?.[activeStep]?.stepName ?? undefined,
        step_ordering: steps?.[activeStep]?.stepId,
      });
    },
    [activeStep, analyticsEventName, analyticsSourcePage, logEvent, steps]
  );

  const handleNext = useCallback(() => {
    if (activeStep === steps.length - 1 && isStandAlone) {
      logEducationEvent(ClickTarget.close);
      setHasSeenFlow(true);
      navigateBack();
    } else if (activeStep === steps.length - 1) {
      logEducationEvent(ClickTarget.start_questionnaire);
      setHasSeenFlow(true);
      setFinishedEducation?.(true);
      setPage?.(nextPage);
    } else {
      logEducationEvent(ClickTarget.next);
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  }, [
    activeStep,
    isStandAlone,
    logEducationEvent,
    navigateBack,
    nextPage,
    setFinishedEducation,
    setHasSeenFlow,
    setPage,
    steps.length,
  ]);

  const handleBack = useCallback(() => {
    logEducationEvent(ClickTarget.back);

    if (activeStep === 0) {
      if (closeOnBack) {
        postClose(closePortalId);
      } else {
        navigateBack();
      }
    }
    setActiveStep((prevActiveStep) => Math.max(prevActiveStep - 1, 0));
  }, [activeStep, closeOnBack, closePortalId, logEducationEvent, navigateBack]);

  const handleSkip = useCallback(() => {
    logEducationEvent(ClickTarget.start_questionnaire);
    setHasSeenFlow(true);
    setFinishedEducation?.(true);
    setPage?.(nextPage);
  }, [logEducationEvent, nextPage, setFinishedEducation, setHasSeenFlow, setPage]);

  return {
    activeStep,
    handleBack,
    handleNext,
    handleSkip,
    bodyRef,
  };
}

export default useEducationFlow;
